import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleInitGame } from "../../service";
import { loadAssets } from "./assetsSlice";
import { loadSounds } from "./soundsSlice";
import { rgsPlay, triggerFakeSpin } from "./spinSlice";
// import { EXPANDING_WILD_ROUND } from "../../devSimulation";

// Async thunk for initializing the game and fetching initial data
export const initializeGame = createAsyncThunk(
  "gameSettings/initializeGame",
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      // Load assets first (blocks loader)
      await dispatch(loadAssets());
      // Wait for sound loading to complete (blocks loader)
      await dispatch(loadSounds());
      const result = await handleInitGame(); // API call to fetch game data

      // Data is loaded - UI logic handled by useWinController.js
      return { result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Heartbeat sync - calls init API to sync balance
 * Does NOT reload assets/sounds, only updates balance
 * CRITICAL: Only extracts balance to avoid unnecessary component re-renders
 * Note: Backend treats this as session keep-alive, but frontend only cares about balance
 */
export const heartbeatSync = createAsyncThunk(
  "gameSettings/heartbeatSync",
  async (_, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      // Return only balance to avoid triggering unnecessary updates
      return { balance: result.balance };
    } catch (error) {
      console.warn("Heartbeat sync failed:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Initial state for the game settings slice
const initialState = {
  token: "",
  balance: 0,
  displayedBalance: 0, // What the UI should show
  bet: 0,
  baseBet: 1, // Base bet for paytable calculations (default to 1 to avoid division by zero)
  bets: [],
  currency: "",
  currencyConfig: {
    subunits: 100, // Number of subunits per main currency unit (default: 100 for cents/dollars)
    exponent: 2, // Number of decimal places to display (default: 2 for "10.00")
    symbol: "", // Currency symbol (e.g., "$", "€", "FUN")
  },
  paytable: [],
  bonusBuyMultiplier: 60, // Bonus buy cost multiplier (60x bet)
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
  },
  initialReels: null, // Static initial reels (10 symbols) set once on init, used by ReelSet for DOM creation
  winningLines: [],
  status: "idle",
  error: null,
  errorCode: null, // Store error code from API (e.g., 301)
  errorDescription: null, // Store error description from API (e.g., "not_enough_money")
  availableActions: ["init", "spin"], // Track available actions from API (spin vs respin)
  flowState: "ready", // Track flow.state from API (ready, closed, freespins)
  features: null, // Track respin features: {respins_issued, respins_left, trigger_screen}
  lastCommand: null, // Track the command sent to API ("spin" or "respin")
  heartbeatError: {
    consecutiveFailures: 0,
    state: "none", // none, warning (3+ failures), critical (10+ failures)
  },
  isResumingHoldAndWin: false, // Track if we're resuming from reload during Hold and Win
};

// Create a slice for managing game settings
const gameSettingsSlice = createSlice({
  name: "gameSettings",
  initialState,
  reducers: {
    incrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex < state.bets.length - 1) {
        state.bet = state.bets[currentIndex + 1];
      }
    },

    decrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex > 0) {
        state.bet = state.bets[currentIndex - 1];
      }
    },

    setBet(state, action) {
      const betValue = action.payload;
      // Validate that the bet value exists in the available bets array
      if (state.bets.includes(betValue)) {
        state.bet = betValue;
      }
    },

    setBaseBet(state, action) {
      state.baseBet = action.payload || 1;
    },

    setBalance(state, action) {
      state.balance = action.payload;
    },

    setDisplayedBalance(state, action) {
      state.displayedBalance = action.payload;
    },

    updateDisplayedBalance(state, action) {
      // Update displayed balance - if payload provided, use it; otherwise sync to actual balance
      state.displayedBalance =
        action.payload !== undefined ? action.payload : state.balance;
    },

    setAvailableActions(state, action) {
      state.availableActions = action.payload;
    },

    setFlowState(state, action) {
      state.flowState = action.payload;
    },

    setLastCommand(state, action) {
      state.lastCommand = action.payload;
    },

    setIsResumingHoldAndWin(state, action) {
      state.isResumingHoldAndWin = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeGame.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isLoading = true;
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Set token
        state.token = result.token;
        state.balance = result.balance;
        state.displayedBalance = result.balance;
        state.bet = result.bet;
        state.baseBet = result.baseBet || 1;
        state.bets = result.bets;
        state.currency = result.currency;

        // Store currency configuration from API response
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits: result.currencyConfig.subunits !== undefined ? result.currencyConfig.subunits : 100,
            exponent: result.currencyConfig.exponent !== undefined ? result.currencyConfig.exponent : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        state.paytable = result.paytable || [];

        // Set bonus buy multiplier if provided
        if (result.bonusBuyMultiplier !== undefined) {
          state.bonusBuyMultiplier = result.bonusBuyMultiplier;
        }

        // Response already transformed by RGS class
        // currentRound is always a single object, never an array
        if (result.round) {
          state.currentRound = result.round;
        }

        // Set initialReels ONCE on init - this is the static value ReelSet uses for DOM creation
        // This value never changes after initialization (10 symbols per reel for animation)
        if (result.initialReels) {
          state.initialReels = result.initialReels;
        }

        // Extract available actions and flow state from init response
        if (result.availableActions) {
          state.availableActions = result.availableActions;
        }

        if (result.flowState) {
          state.flowState = result.flowState;
        }

        // CRITICAL: Detect Hold and Win reload
        // Check if we're reloading into an active Hold and Win session
        const isHoldAndWinReload =
          result.flowState === "respin" &&
          result.availableActions?.includes("respin") &&
          result.features?.respins_left !== undefined;

        // Store command from init
        if (result.command) {
          state.lastCommand = result.command;
        }

        if (isHoldAndWinReload) {
          // Set resume flag
          state.isResumingHoldAndWin = true;

          // Store features data (respins_left, trigger_screen, etc.)
          state.features = result.features;

          // CRITICAL: Close intro popup and open reload dialog
          // This must be done via thunk's dispatch, not directly in reducer
          // We'll handle this in the thunk itself
        } else {
          // Normal base game init
          state.isResumingHoldAndWin = false;
          state.features = null;
        }

        state.status = "succeeded";
        state.isLoading = false;
        state.winningLines = result.winningLines || [];
      })
      .addCase(initializeGame.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to initialize the game";
        state.isLoading = false;
      })

      // Listen for spin actions from spinSlice
      .addCase(rgsPlay.pending, (state, action) => {
        // Get bonusCost from action.meta.arg if it's a bonus buy, otherwise use regular bet
        const deductionAmount = action.meta.arg?.bonusCost || state.bet;

        // Ensure balance is a valid number (prevent NaN)
        const currentBalance = Number(state.balance) || 0;
        const deduction = Number(deductionAmount) || 0;

        // Only deduct balance if we have enough credits
        // If insufficient, still send API request but don't deduct balance
        // API will handle insufficient credits and return proper response
        if (currentBalance >= deduction) {
          // First: Force sync displayed balance to actual balance (cancels ongoing animations)
          state.displayedBalance = currentBalance;

          // Then: Subtract deduction amount from both balances atomically
          state.balance = currentBalance - deduction;
          state.displayedBalance = currentBalance - deduction;
        }

        state.error = null;
        state.status = "spinning";
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const {
          token,
          balance,
          bet,
          round,
          fake,
          availableActions,
          flowState,
          features: featuresPayload,
          command,
        } = action.payload;

        // Update token when new token received
        if (!fake && token && token !== state.token) {
          state.token = token;
        }

        state.bet = bet !== undefined ? bet : state.bet;

        // Handle fake spins - don't update balance from API response
        if (fake) {
          if (round) {
            state.currentRound = round;
          }
        } else {
          // Regular spin: Use API balance (trust server)
          state.balance = balance !== undefined ? balance : state.balance;

          // Store the round
          if (round) {
            state.currentRound = round;
          // state.currentRound = EXPANDING_WILD_ROUND;
          }

          // Store features for respin tracking (respins_issued, respins_left, trigger_screen)
          if (featuresPayload) {
            state.features = featuresPayload;
          } else {
            // Clear features when not present (regular game, no respins)
            state.features = null;
          }

          // Update available actions after each spin
          if (availableActions) {
            state.availableActions = availableActions;
          }

          if (flowState) {
            state.flowState = flowState;
          }

          // Store command that was sent to API
          if (command) {
            state.lastCommand = command;
          }
        }

        state.status = "succeeded";
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        const errorData = action.payload;

        // Parse error response: {"errors":[{"code":301,"desc":"not_enough_money"}]}
        if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const firstError = errorData.errors[0];
          state.errorCode = firstError.code;
          state.errorDescription = firstError.desc;
          state.error = `Error ${firstError.code}: ${firstError.desc}`;
        } else {
          state.error = errorData || "Spin failed";
          state.errorCode = null;
          state.errorDescription = null;
        }

        state.status = "failed";
      })

      // Handle fake spin action - set fake round data immediately
      .addCase(triggerFakeSpin, (state, action) => {
        // Set fake round data in game state for reel animation
        state.currentRound = {
          bet: state.bet,
          win: 0,
          wins: {},
          slotface: [
            [6, 6, 6], // Reel 1: All 6s
            [7, 7, 7], // Reel 2: All 7s
            [8, 8, 8], // Reel 3: All 8s
            [9, 9, 9], // Reel 4: All 9s
            [11, 11, 11], // Reel 5: All 11s
          ],
          reels: [
            [6, 6, 6, 6, 6, 6], // Reel 1: All 6s
            [7, 7, 7, 7, 7, 7], // Reel 2: All 7s
            [8, 8, 8, 8, 8, 8], // Reel 3: All 8s
            [9, 9, 9, 9, 9, 9], // Reel 4: All 9s
            [11, 11, 11, 11, 11, 11], // Reel 5: All 11s
          ],
          id: `fake-${Date.now()}`,
        };

        state.status = "succeeded";
      })

      // Handle heartbeat sync - update ONLY balance
      .addCase(heartbeatSync.fulfilled, (state, action) => {
        const { balance } = action.payload;

        // CRITICAL: Update ONLY balance to avoid unnecessary re-renders
        // Do NOT update any other fields even if they exist in the response
        if (balance !== undefined) {
          state.balance = balance;
          // Also update displayedBalance so UI reflects the change immediately
          state.displayedBalance = balance;
        }

        // Reset error state on successful heartbeat
        state.heartbeatError.consecutiveFailures = 0;
        state.heartbeatError.state = "none";
      })
      .addCase(heartbeatSync.rejected, (state, action) => {
        // Increment consecutive failure counter
        state.heartbeatError.consecutiveFailures += 1;

        // Update error state based on failure count
        if (state.heartbeatError.consecutiveFailures >= 10) {
          state.heartbeatError.state = "critical";
        } else if (state.heartbeatError.consecutiveFailures >= 3) {
          state.heartbeatError.state = "warning";
        }

        // Don't set game status to failed - heartbeat errors should not block gameplay
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectToken = (state) => state.game.token;
export const selectBalance = (state) => state.game.balance;
export const selectDisplayedBalance = (state) => state.game.displayedBalance;
export const selectBet = (state) => state.game.bet;
export const selectBaseBet = (state) => state.game.baseBet || 1;
export const selectBets = (state) => state.game.bets;
export const selectCurrency = (state) => state.game.currency;
export const selectCurrencyConfig = (state) => state.game.currencyConfig;
export const selectPaytable = (state) => state.game.paytable;
export const selectBonusBuyMultiplier = (state) => state.game.bonusBuyMultiplier;
export const selectCurrentRound = (state) => state.game.currentRound;
export const selectInitialReels = (state) => state.game.initialReels;
export const selectWinningLines = (state) => state.game.winningLines;
export const selectGameStatus = (state) => state.game.status;
export const selectGameError = (state) => state.game.error;
export const selectErrorCode = (state) => state.game.errorCode;
export const selectErrorDescription = (state) => state.game.errorDescription;
export const selectAvailableActions = (state) => state.game.availableActions;
export const selectFlowState = (state) => state.game.flowState;
export const selectFeatures = (state) => state.game.features;
export const selectLastCommand = (state) => state.game.lastCommand;
export const selectHeartbeatError = (state) => state.game.heartbeatError;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  incrementBet,
  decrementBet,
  setBalance,
  setBet,
  setBaseBet,
  setDisplayedBalance,
  updateDisplayedBalance,
  setAvailableActions,
  setFlowState,
  setLastCommand,
  setIsResumingHoldAndWin,
} = gameSettingsSlice.actions;

// Export the reducer to integrate into the Redux store
export default gameSettingsSlice.reducer;
